import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, Clock, MapPin, Wifi, Shield, AlertTriangle, RefreshCw, LogOut, Filter } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const SessionManagementPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [sessionToTerminate, setSessionToTerminate] = useState(null);
  const { user } = useAuth();

  // Mock session data - in a real implementation, fetch from your auth/session store
  // (for example: Firebase Auth tokens / Firestore session documents or a backend session API)
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockSessions = [
        {
          id: '1',
          session_token: 'sess_current_session',
          device_info: {
            device_type: 'desktop',
            browser: 'Chrome 120',
            os: 'Windows 11',
            screen_resolution: '1920x1080'
          },
          ip_address: '192.168.1.100',
          location_data: {
            city: 'Mumbai',
            country: 'India',
            timezone: 'Asia/Kolkata'
          },
          status: 'active',
          last_activity: new Date()?.toISOString(),
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000)?.toISOString(), // 2 hours ago
          is_current: true
        },
        {
          id: '2',
          session_token: 'sess_mobile_session',
          device_info: {
            device_type: 'mobile',
            browser: 'Safari Mobile',
            os: 'iOS 17.1',
            screen_resolution: '390x844'
          },
          ip_address: '192.168.1.105',
          location_data: {
            city: 'Mumbai',
            country: 'India',
            timezone: 'Asia/Kolkata'
          },
          status: 'active',
          last_activity: new Date(Date.now() - 30 * 60 * 1000)?.toISOString(), // 30 minutes ago
          created_at: new Date(Date.now() - 4 * 60 * 60 * 1000)?.toISOString(), // 4 hours ago
          is_current: false
        },
        {
          id: '3',
          session_token: 'sess_tablet_session',
          device_info: {
            device_type: 'tablet',
            browser: 'Chrome Mobile',
            os: 'Android 13',
            screen_resolution: '768x1024'
          },
          ip_address: '192.168.1.110',
          location_data: {
            city: 'Mumbai',
            country: 'India',
            timezone: 'Asia/Kolkata'
          },
          status: 'expired',
          last_activity: new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString(), // 24 hours ago
          created_at: new Date(Date.now() - 25 * 60 * 60 * 1000)?.toISOString(), // 25 hours ago
          is_current: false
        }
      ];
      setSessions(mockSessions);
      setLoading(false);
    }, 1000);
  };

  const handleTerminateSession = async (sessionId) => {
    setLoading(true);
    try {
      // Simulate API call to terminate session
      setTimeout(() => {
        setSessions(prev => prev?.map(session => 
          session?.id === sessionId 
            ? { ...session, status: 'terminated', terminated_at: new Date()?.toISOString() }
            : session
        ));
        setLoading(false);
        setShowTerminateModal(false);
        setSessionToTerminate(null);
      }, 1000);
    } catch (error) {
      setLoading(false);
      console.error('Failed to terminate session:', error);
    }
  };

  const handleTerminateAllOther = async () => {
    setLoading(true);
    try {
      // Simulate API call to terminate all other sessions
      setTimeout(() => {
        setSessions(prev => prev?.map(session => 
          !session?.is_current 
            ? { ...session, status: 'terminated', terminated_at: new Date()?.toISOString() }
            : session
        ));
        setLoading(false);
      }, 1500);
    } catch (error) {
      setLoading(false);
      console.error('Failed to terminate sessions:', error);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="h-5 w-5" />;
      case 'tablet':
        return <Tablet className="h-5 w-5" />;
      default:
        return <Monitor className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      case 'terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const filteredSessions = sessions?.filter(session => {
    if (filterType === 'all') return true;
    if (filterType === 'active') return session?.status === 'active';
    if (filterType === 'inactive') return session?.status !== 'active';
    if (filterType === 'mobile') return session?.device_info?.device_type === 'mobile';
    if (filterType === 'desktop') return session?.device_info?.device_type === 'desktop';
    return true;
  });

  const activeSessions = sessions?.filter(s => s?.status === 'active')?.length || 0;
  const currentSession = sessions?.find(s => s?.is_current);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Session Management</h2>
          <p className="text-gray-600">Monitor and manage your active sessions across devices</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadSessions}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleTerminateAllOther}
            disabled={loading || activeSessions <= 1}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Terminate All Others</span>
          </button>
        </div>
      </div>

      {/* Session Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Sessions</p>
              <p className="text-2xl font-bold text-green-600">{activeSessions}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Shield className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sessions</p>
              <p className="text-2xl font-bold text-blue-600">{sessions?.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Monitor className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Last Activity</p>
              <p className="text-2xl font-bold text-gray-900">
                {currentSession ? formatTime(currentSession?.last_activity) : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <Clock className="h-6 w-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-4">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Sessions</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
          </select>
        </div>
      </div>

      {/* Sessions List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Session Details</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading sessions...</p>
          </div>
        ) : filteredSessions?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No sessions found
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredSessions?.map((session) => (
              <div key={session?.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className={`p-3 rounded-full ${
                        session?.is_current ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {getDeviceIcon(session?.device_info?.device_type)}
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="text-lg font-medium text-gray-900">
                          {session?.device_info?.browser} on {session?.device_info?.os}
                        </h4>
                        {session?.is_current && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Current Session
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session?.status)}`}>
                          {session?.status?.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Wifi className="h-4 w-4" />
                            <span>IP: {session?.ip_address}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4" />
                            <span>{session?.location_data?.city}, {session?.location_data?.country}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span>Last active: {formatTime(session?.last_activity)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Monitor className="h-4 w-4" />
                            <span>Resolution: {session?.device_info?.screen_resolution}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 text-xs text-gray-500">
                        Started: {(() => { const d = new Date(session?.created_at); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()}
                        {session?.terminated_at && (
                          <span className="ml-4">
                            Terminated: {(() => { const d = new Date(session?.terminated_at); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    {session?.status === 'active' && !session?.is_current && (
                      <button
                        onClick={() => {
                          setSessionToTerminate(session);
                          setShowTerminateModal(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Terminate session"
                      >
                        <LogOut className="h-4 w-4" />
                      </button>
                    )}
                    
                    {session?.status !== 'active' && (
                      <div className="flex items-center space-x-1 text-gray-400">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-xs">Inactive</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Terminate Session Modal */}
      {showTerminateModal && sessionToTerminate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Terminate Session</h3>
                  <p className="text-gray-600">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="text-sm">
                  <div className="font-medium text-gray-900 mb-1">
                    {sessionToTerminate?.device_info?.browser} on {sessionToTerminate?.device_info?.os}
                  </div>
                  <div className="text-gray-600">
                    IP: {sessionToTerminate?.ip_address}
                  </div>
                  <div className="text-gray-600">
                    Location: {sessionToTerminate?.location_data?.city}, {sessionToTerminate?.location_data?.country}
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to terminate this session? The user will be logged out immediately and will need to sign in again.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowTerminateModal(false);
                    setSessionToTerminate(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleTerminateSession(sessionToTerminate?.id)}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Terminating...' : 'Terminate Session'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionManagementPanel;